<template>
  <WidgetGrid :background-component="LandingBackground" />
</template>

<script setup lang="ts">
import {
  useWidgetGridStore,
  WidgetGrid,
  WidgetGridVariants,
  type WidgetLayout,
} from "@modernary/mui/components/widgetGrid";
import LandingBackground from "@/components/LandingBackground.vue";
import "gridstack/dist/gridstack.min.css";
import { widgetKeys, widgetRegistry } from "@/constants/widgetRegistry";
import { onMounted, computed, watch } from "vue";
import { useInternalLandingStore } from "@/components/internalLandingStore";
import { useAppNav } from "@modernary/mui/components/common";
import { routeNames } from "@/routes";

const homeInternalDefaultLayout: WidgetLayout[] = [
  {
    id: "widget-welcome",
    key: widgetKeys.internalGreeting,
    size: {
      w: 5,
      h: 20,
      minW: 1,
      minH: 4,
    },
  },
  {
    id: "widget-quick-links",
    key: widgetKeys.solidWasteQuickLinks,
    size: {
      w: 1,
      h: 24,
      minW: 1,
      minH: 3,
    },
    sizeToContent: true,
  },
  {
    id: "widget-sustainability-quick-links",
    key: widgetKeys.sustainabilityQuickLinks,
    size: {
      w: 1,
      h: 24,
      minW: 1,
      minH: 3,
    },
    sizeToContent: true,
  },
];

const widgetGridStore = useWidgetGridStore();
const internalLandingStore = useInternalLandingStore();
const { navigationItems } = useAppNav();

const solidWasteNavItems = computed(() => {
  const solidWaste = navigationItems?.find((x) => x.label === "Solid Waste");
  const items = solidWaste?.items.filter(
    (x) =>
      x.name === routeNames.permit.permits ||
      x.name === routeNames.disposalReport.disposalReports ||
      x.name === routeNames.facilitySite.facilitySites ||
      x.name === routeNames.inspection.inspections ||
      x.name === routeNames.report.reports,
  );
  return {
    items: items || [],
    isAuthPending: solidWaste?.isAuthPending ?? true,
  };
});

const sustainabilityNavItems = computed(() => {
  const sustainability = navigationItems?.find((x) => x.label === "Sustainability");
  return {
    items: sustainability?.items || [],
    isAuthPending: sustainability?.isAuthPending ?? true,
  };
});

const isAuthPending = computed(() => {
  return solidWasteNavItems.value.isAuthPending || sustainabilityNavItems.value.isAuthPending;
});

onMounted(() => {
  widgetGridStore.isLoading = true;
  widgetGridStore.widgetRegistry = widgetRegistry;
  widgetGridStore.configureGrid({
    variant: WidgetGridVariants.Dashboard,
    storageKey: "smart-internal-dashboard-layout",
    defaultLayout: homeInternalDefaultLayout,
    layoutVersion: 1,
  });

  watch(
    [solidWasteNavItems, sustainabilityNavItems, isAuthPending],
    ([solidWaste, sustainability, authPending]) => {
      const solidWasteLinks = solidWaste.items
        .filter((item) => item.isAuthorized)
        .map((item) => ({
          title: item.label,
          description: item.description,
          to: { name: item.name },
        }));

      const sustainabilityLinks = sustainability.items
        .filter((item) => item.isAuthorized)
        .map((item) => ({
          title: item.label,
          description: item.description,
          to: { name: item.name },
        }));

      internalLandingStore.solidWasteLinks = solidWasteLinks;
      internalLandingStore.sustainabilityLinks = sustainabilityLinks;

      if (!authPending) {
        widgetGridStore.isLoading = false;
      }
    },
    { immediate: true },
  );
});
</script>
